import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import FloatingCTA from "@/components/feature/FloatingCTA";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import SEO from "@/components/feature/SEO";
import WhatsAppWidget from "@/components/feature/WhatsAppWidget";
import { webpageSchema, breadcrumbSchema, faqPageSchema, siteUrl } from "@/lib/schemas";
import { Phone, FileText, ClipboardCheck, TrendingUp, Shield, CheckCircle, ArrowRight, Clock, User } from "lucide-react";

const claimSteps = [
  { num: "01", title: "Report the Claim", desc: "Call our 24x7 claims helpline at +91 99993 65655 or WhatsApp us immediately. We guide you through the initial documentation.", icon: Phone },
  { num: "02", title: "Document Collection", desc: "We help you gather all required documents — hospital bills, police FIR, diagnostic reports, discharge summaries, and more.", icon: FileText },
  { num: "03", title: "Claim Submission", desc: "Our team fills out claim forms, verifies documents for completeness, and submits to the insurer/TPA on your behalf.", icon: ClipboardCheck },
  { num: "04", title: "Follow-up & Tracking", desc: "We actively follow up with the insurer, track claim status, and push for faster processing and settlement.", icon: TrendingUp },
  { num: "05", title: "Settlement", desc: "Once approved, we ensure you receive the claim amount via NEFT/cheque. In case of rejection, we handle appeals and re-submissions.", icon: Shield },
];

const services = [
  { icon: Shield, title: "Cashless Claims", desc: "We coordinate with network hospitals and TPAs to ensure smooth cashless treatment without upfront payment hassles." },
  { icon: FileText, title: "Reimbursement Support", desc: "For non-network hospitals, we meticulously organize documents and submit reimbursement claims with zero errors." },
  { icon: ClipboardCheck, title: "Documentation Guidance", desc: "Not sure what papers you need? We provide a personalized checklist and review every document before submission." },
  { icon: Clock, title: "Claim Tracking", desc: "Track your claim status in real-time. We provide regular updates and proactively address any insurer queries." },
  { icon: TrendingUp, title: "Escalation Support", desc: "Claim stuck or delayed? We escalate to grievance cells, IRDAI ombudsman, and senior management for resolution." },
  { icon: User, title: "Dedicated Claims Advisor", desc: "Every claim gets a dedicated advisor who stays with you from intimation to settlement." },
];

const testimonials = [
  { name: "Ramesh Gupta", city: "Delhi", type: "Health Claim", amount: "₹4.2 Lakhs", text: "My father's bypass surgery claim was initially rejected. ExpertAdvise stepped in, identified the missing documents, and got the full ₹4.2L approved within 10 days." },
  { name: "Sunita Sharma", city: "Mumbai", type: "Car Claim", amount: "₹1.8 Lakhs", text: "After a major accident, I was overwhelmed. Their team handled everything — surveyor coordination, document submission, and follow-up. Claim settled in 12 days." },
  { name: "Vikram Patel", city: "Ahmedabad", type: "Travel Claim", amount: "₹85,000", text: "Missed my connecting flight in Europe due to delays. They guided me through the travel claim process and I got reimbursed within a week." },
];

const claimFaqs = [
  { question: "How long does an insurance claim take to settle?", answer: "Cashless health claims at network hospitals typically settle within 2-4 hours for pre-authorization and 7-15 days for final settlement. Reimbursement claims take 15-30 days depending on document completeness. Car and travel claims vary by complexity. Our team accelerates every step." },
  { question: "What documents are needed for a health insurance claim?", answer: "For cashless: ID proof, health card, hospital admission form. For reimbursement: discharge summary, all bills and receipts, diagnostic reports, prescriptions, FIR (if accident), and canceled cheque. We provide a personalized checklist based on your specific claim type." },
  { question: "Can ExpertAdvise help with rejected claims?", answer: "Absolutely. Our claims team has a 97.8% success rate in claim recovery. We analyze rejection reasons, gather additional evidence, file appeals with the insurer's grievance cell, and escalate to IRDAI ombudsman if needed." },
  { question: "Is there a fee for claims assistance?", answer: "No. Claims assistance is completely free for all ExpertAdvise customers who purchased or renewed their policy through us. For policies bought elsewhere, we offer claims support at a nominal service fee." },
];

export default function ClaimsAssistancePage() {
  const baseUrl = siteUrl;
  const pageUrl = `${baseUrl}/claims-assistance`;

  const schemas = [
    webpageSchema(
      "Claims Assistance Center | ExpertAdvise.in — 97.8% Success Rate",
      "Get expert claims assistance for health, car, travel, and life insurance. Cashless coordination, reimbursement support, documentation guidance, and rejection appeals. Call +91 99993 65655.",
      pageUrl
    ),
    faqPageSchema(claimFaqs),
    breadcrumbSchema([
      { name: "Home", url: baseUrl },
      { name: "Claims Assistance", url: pageUrl },
    ]),
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO
        title="Claims Assistance Center | ExpertAdvise.in — 97.8% Success Rate"
        description="Get expert claims assistance for health, car, travel, and life insurance. Cashless coordination, reimbursement support, documentation guidance, and rejection appeals. Call +91 99993 65655."
        keywords="insurance claim assistance, cashless claim help, health insurance claim, claim rejection appeal, insurance claim advisor"
        canonical={pageUrl}
        ogUrl={pageUrl}
        schemas={schemas}
      />
      <Navbar />

      <section className="pt-24 md:pt-32 pb-10 md:pb-14 bg-gradient-to-br from-primary-600 to-primary-800 text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto">
            <Breadcrumbs
              items={[
                { name: "Home", href: "/" },
                { name: "Claims Assistance" },
              ]}
              className="justify-center mb-4"
            />
            <span className="inline-block bg-white/15 text-white text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full mb-3">Claims Support</span>
            <h1 className="text-2xl md:text-4xl font-bold mb-4">Expert Claims Assistance Center</h1>
            <p className="text-white/80 text-base md:text-lg leading-relaxed mb-6">
              Filing an insurance claim can be stressful. Our dedicated claims team handles everything — from documentation to follow-up to settlement — so you can focus on recovery.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a href="tel:+919999365655" className="bg-white text-primary-700 hover:bg-white/90 font-semibold px-6 py-3 rounded-full transition-colors whitespace-nowrap flex items-center gap-2">
                <Phone className="w-4 h-4" />Call Claims Helpline
              </a>
              <WhatsAppWidget variant="inline" customMessage="I need Claims Assistance. Please help me with my insurance claim process.">
                <i className="ri-whatsapp-line text-lg"></i>
                WhatsApp Claim Help
              </WhatsAppWidget>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center max-w-2xl mx-auto mb-10 md:mb-14">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">Our Claims Process</h2>
            <p className="text-slate-600">A simple, transparent 5-step process designed to get your claim settled fast and hassle-free.</p>
          </div>
          <div className="space-y-6">
            {claimSteps.map((step, i) => (
              <div key={i} className="flex gap-4 md:gap-6">
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 rounded-full bg-primary-600 text-white flex items-center justify-center text-sm font-bold shrink-0">{step.num}</div>
                  {i < claimSteps.length - 1 && <div className="w-0.5 flex-1 bg-primary-200 my-2" />}
                </div>
                <div className="bg-white rounded-2xl border border-slate-200 p-5 md:p-6 flex-1 hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-3 mb-2">
                    <step.icon className="w-5 h-5 text-primary-600" />
                    <h3 className="text-lg font-bold text-slate-900">{step.title}</h3>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-white border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center max-w-2xl mx-auto mb-10 md:mb-14">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">Claims Services We Offer</h2>
            <p className="text-slate-600">End-to-end claims management across all insurance categories.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {services.map((s, i) => (
              <div key={i} className="bg-slate-50 rounded-2xl p-6 border border-slate-100 hover:border-primary-200 hover:shadow-lg transition-all duration-300">
                <div className="w-11 h-11 rounded-xl bg-primary-50 flex items-center justify-center mb-4"><s.icon className="w-5 h-5 text-primary-600" /></div>
                <h3 className="text-base font-bold text-slate-900 mb-2">{s.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center max-w-2xl mx-auto mb-10 md:mb-14">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">Claim Success Stories</h2>
            <p className="text-slate-600">Real customers, real claims, real results.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {testimonials.map((t, i) => (
              <div key={i} className="bg-white rounded-2xl border border-slate-200 p-6">
                <div className="flex items-center gap-2 mb-3">
                  <span className="bg-emerald-50 text-emerald-700 text-xs font-bold px-2 py-1 rounded-full">{t.type}</span>
                  <span className="text-xs text-slate-400">{t.city}</span>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed mb-4 italic">"{t.text}"</p>
                <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                  <div className="text-sm font-semibold text-slate-900">{t.name}</div>
                  <div className="text-sm font-bold text-emerald-600">{t.amount}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-primary-50">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-3">Facing a Claim Issue? We Can Help.</h2>
          <p className="text-slate-600 mb-6">Whether it's a fresh claim, a delayed settlement, or a rejection appeal — our team has a 97.8% success rate in claim recovery.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a href="tel:+919999365655" className="bg-cta-600 hover:bg-cta-700 text-white font-semibold px-6 py-3 rounded-full transition-colors whitespace-nowrap flex items-center gap-2">
              <Phone className="w-4 h-4" />Call Now
            </a>
            <Link to="/book-appointment?source=Claims%20Assistance%20Page" className="bg-white hover:bg-slate-50 text-slate-700 font-semibold px-6 py-3 rounded-full border border-slate-200 transition-colors whitespace-nowrap flex items-center gap-2">
              Book Consultation<ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingCTA />
    </div>
  );
}